import PropTypes from 'prop-types';
import { useContext, useEffect, useState } from 'react';
import { FormattedTime, useIntl } from 'react-intl';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import { Link, NavLink, useHistory, useLocation } from 'react-router-dom';
import { UserContext } from 'contexts/user/UserContext';
import { useMenu } from 'contexts/MenuContextUtils';
import { useSettings } from 'contexts/SettingsContextUtils';
import { localeOptions } from 'constants/defaultValues';
import MappingToolIcon from 'assets/icons/mapping-icons/MappingToolIcon';
import { MenuIcon, MobileMenuIcon } from 'components/svg';
import { gql, useMutation } from '@apollo/client';
import InfoModal from 'components/Modal/InfoModal';
import IntlMessages from 'helpers/IntlMessages';
import { getDirection, setDirection } from 'helpers/Utils';
import { isDev } from 'helpers/helper';
import TranslateIcon from 'mdi-react/TranslateIcon';
import ChatOutlineIcon from 'mdi-react/ChatOutlineIcon';
import AccountGroup from 'mdi-react/AccountGroupIcon';
import PlayCircleOutlineIcon from 'mdi-react/PlayCircleOutlineIcon';
import AccountOutlineIcon from 'mdi-react/AccountOutlineIcon';
import SettingsIcon from 'mdi-react/SettingsIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import LogoutVariantIcon from 'mdi-react/LogoutVariantIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import CustomDropdownToggle from 'components/custom/CustomDropdownToggle';
import NotificationInbox from 'components/NotificationInbox';
import { useAuth } from 'contexts/AuthContextUtils';
import useCurrentlyPlayingQuests from 'pages/Commons/Quest/useCurrentlyPlayingQuests';

const UPDATE_USER_LANGUAGE = gql`
mutation updateUserLanguage(
  $id: UUID!
  $language: String!
) {
  updateUser(input: { patch: { language: $language }, id: $id }) {
    user {
      username
      language
    }
  }
}
`;

const RESEND_VERIFICATION_EMAIL = gql`
mutation MyMutation($emailId: UUID!) {
  resendEmailVerificationCode(input: {emailId: $emailId}) {
    success
  }
}`

const TopNav = () => {
  const history = useHistory();
  const location = useLocation();
  const { locale, setLocale } = useSettings();
  const showVerifyBanner = new URLSearchParams(location.search).has('unverified');
  const {
    toggleMenu,
  } = useMenu();
  const { isAuthenticated, currentUser, refetchCurrentUser } = useContext(UserContext);
  const intl = useIntl();
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [appLocaleOptions, setAppLocaleOptions] = useState({ id: null, name: locale, direction: null });
  const [verificationEmailResent, setVerificationEmailResent] = useState(false);
  const [verificationEmailResentError, setVerificationEmailResentError] = useState(false);
  const [showPlayingQuestsModal, setShowPlayingQuestsModal] = useState(false);
  const [updateUserLanguage] = useMutation(UPDATE_USER_LANGUAGE);
  const [resendVerificationEmail] = useMutation(RESEND_VERIFICATION_EMAIL);
  const { logout } = useAuth();
  const discourseUrl = import.meta.env.VITE_DISCOURSE_URL || '';
  const defaultChatUrl = import.meta.env.VITE_DISCOURSE_CHAT_URL || '';
  const chatUrl = defaultChatUrl || (discourseUrl ? `${discourseUrl}/chat` : '');
  const canAccessDiscourse = isAuthenticated && !!discourseUrl;
  const shouldCheckDiscourseChatMessages = import.meta.env.VITE_DISCOURSE_CHECK_CHAT_MESSAGES || false;
  const isAutomatedBrowser = typeof navigator !== 'undefined' && navigator.webdriver;
  const canCheckDiscourseChatMessages = canAccessDiscourse && shouldCheckDiscourseChatMessages && !isAutomatedBrowser;
  const [unreadChatCount, setUnreadChatCount] = useState(0);
  const { error: playingQuestsError, hasPlayingQuests, quests: playingQuests } = useCurrentlyPlayingQuests(currentUser?.id, locale);
  const logoutRedirectUrl = import.meta.env.VITE_HOME_URL || (isDev ? '/login' : '/page/about');

  const handleChangeLocale = () => setShowInfoModal(true);

  const handleLogout = async () => {
    try {
      await logout().then(() => {
        refetchCurrentUser();
      });
      history.push('/logout'); // first go to a non-private page, so that logout doesn't trigger a redirect to login
      window.location.href = logoutRedirectUrl;
    } catch (error) {
      return console.error('Error on logging out:', error);
    }
  };

  const handleSettings = () => history.push('/profile/edit');

  const handleProfile = () => history.push('/profile');

  const handlePlaying = () => setShowPlayingQuestsModal(true);
  const handleTranslations = () => history.push('/translations');

  const handleClickLogin = (e) => {
    e.preventDefault();
    history.push({
      pathname: '/login',
      state: {
        from: history.location.pathname,
      }
    });
  };

  const handleAbout = () => {
    window.location.href = '/about';
  };

  const handleContact = () => {
    window.location.href = '/page/contact-us';
  };

  const menuButtonClick = (e) => {
    e.preventDefault();
    toggleMenu();

    setTimeout(() => {
      const event = document.createEvent('HTMLEvents');
      event.initEvent('resize', false, false);
      window.dispatchEvent(event);
    }, 350);
  };

  const mobileMenuButtonClick = (e) => {
    e.preventDefault();
    toggleMenu();
  };

  const handleConfirm = () => {
    setShowInfoModal(false);

    if (appLocaleOptions.id) {

      // for logged in user, store the language in the database
      if (isAuthenticated) {
        currentUser.id && updateUserLanguage({
          variables: {
            id: currentUser.id,
            language: appLocaleOptions.id,
          }
        }).then(() => {
          // only change the app language and save to local storage AFTER the mutation finishes,
          // otherwise the useEffect below would change it back to pre-mutation database value
          setLocale(appLocaleOptions.id);
          // reload the page, so that we are sure that all forms will be loaded/saved with correct language settings
          window.location.reload();
        });
      } else {
        // for non-logged in user
        setLocale(appLocaleOptions.id);
      }
    }

    const currentDirection = getDirection().direction;
    if (appLocaleOptions.direction && appLocaleOptions.direction !== currentDirection) {
      setDirection(appLocaleOptions.direction);
      setTimeout(() => window.location.reload(), 500);
    }
  };

  const ChangeLanguageModalContent = () => {
    const intl = useIntl();

    return (
      <div className="text-center">
        <TranslateIcon
          size={100}
          className="mb-2 accent-color"
        />
        <div className="text-normal mb-4">
          <p>{intl.formatMessage({ id: 'nav.modal-about-change-language' }, { locale: appLocaleOptions.name?.split(' -')[0] })}</p>
          <p><b>{intl.formatMessage({ id: 'nav.modal-about-change-language-saved-changes' })}</b></p>
        </div>
      </div>
    );
  };


  /* set language */
  // if user.language is null, update the value according to locale
  // if user.language is not null and the value is different from locale, update locale according to user.language
  useEffect(() => {
    if (currentUser?.language && currentUser?.language !== locale) {
      setLocale(currentUser.language);
    } else if (currentUser?.id && !currentUser?.language) {
      updateUserLanguage({
        variables: { id: currentUser.id, language: locale }
      });
    }
  }, [setLocale, updateUserLanguage, currentUser, locale]);

  useEffect(() => {
    if (!canCheckDiscourseChatMessages || !currentUser?.id) {
      setUnreadChatCount(0);
      return undefined;
    }

    let cancelled = false;
    let inFlight = false;

    const fetchUnreadChatCount = async () => {
      if (inFlight || cancelled) {
        return;
      }

      inFlight = true;

      try {
        const response = await fetch(
          '/api/discourse/chat/unread',
          {
            cache: 'no-store',
            credentials: 'include',
            headers: {
              Accept: 'application/json',
              'Cache-Control': 'no-cache',
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Discourse unread chat request failed with ${response.status}`
          );
        }

        const data = await response.json();

        if (!cancelled) {
          const unreadCount = Number(
            data?.unreadCount || 0
          );

          setUnreadChatCount(
            Number.isFinite(unreadCount)
            ? unreadCount
            : 0
          );
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error(
            'Failed to load Discourse unread chat count:',
            error
          );
        }

        if (!cancelled) {
          setUnreadChatCount(0);
        }
      } finally {
        inFlight = false;
      }
    };

    fetchUnreadChatCount();

    const intervalId = window.setInterval(
      fetchUnreadChatCount,
      60000
    );

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [canCheckDiscourseChatMessages, currentUser?.id, discourseUrl]);

  useEffect(() => {
    if (playingQuestsError) {
      console.error('Failed to load currently playing quests:', playingQuestsError);
    }
  }, [playingQuestsError]);

  return (
    <>
      <nav className="navbar fixed-top">
        <div className="d-flex align-items-center navbar-left">
          <NavLink
            to="#"
            location={{}}
            className="menu-button d-none d-md-block"
            onClick={menuButtonClick}
          >
            <MenuIcon />
          </NavLink>
          <NavLink
            to="#"
            location={{}}
            className="menu-button-mobile d-xs-block d-sm-block d-md-none"
            onClick={mobileMenuButtonClick}
          >
            <MobileMenuIcon />
          </NavLink>
          <a className="navbar-logo" href="/">
            <span className="logo d-none d-xs-block" />
            <span className="logo-mobile d-block d-xs-none" />
          </a>

        </div>

        {/* Icons group */}
        <div className="navbar-actions">
          {/* mapping icon */}
          <Link className="navbar-action-item" to='/map' aria-label={intl.formatMessage({ id: 'mapping.mapping-tool' })}>
            <MappingToolIcon size='30' id='mappingIconNavBar' />
          </Link>

          {/* chat icon */}
          {canAccessDiscourse && (
            <a
              href={chatUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="navbar-action-item position-relative chat-icon-link"
              aria-label={intl.formatMessage({ id: 'chat' })}
            >
              <ChatOutlineIcon size='30' id='chatIconNavBar' />
              {unreadChatCount > 0 &&
               <div className='badge badge-top-right-unread-messages'>&nbsp;</div>
              }
            </a>
          )}

          {/* forum icon */}
          {canAccessDiscourse && (
            <a
              href={discourseUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="navbar-action-item position-relative chat-icon-link"
              aria-label={intl.formatMessage({ id: 'general.forum' })}
            >
              <AccountGroup size='30' id='forumIconNavBar' />
            </a>
          )}

          {/* search icon */}
          {isAuthenticated && (
            <Link className="navbar-action-item" to='/search' aria-label={intl.formatMessage({ id: 'general.search' })}>
              <MagnifyIcon size='30' id='searchIconNavBar' />
            </Link>
          )}

          {/* notifications inbox */}
          <div className="navbar-action-item">
            <NotificationInbox />
          </div>

          {/* language dropdown */}
          <div className="navbar-action-item">
            <Dropdown>
              <Dropdown.Toggle size="sm" className="language-button">
                <TranslateIcon className='color-theme-1' size='20' />
              </Dropdown.Toggle>
              <Dropdown.Menu className="mt-1" right>
                {localeOptions.map((l) => {
                  return (
                    <Dropdown.Item active={locale === l.id} onClick={() => {
                      setAppLocaleOptions({ id: l.id, name: l.name, direction: l.direction });
                      handleChangeLocale();
                    }} key={l.id}>
                      {l.name}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </div>

          {/* login/profile manage dropdown */}
          <div className="navbar-action-item user d-inline-block">
            {!isAuthenticated ? (
              <Link to="/login">
                <Button outline="true" color="success" className="text-white mr-3"
                  onClick={(e) => {
                    handleClickLogin(e);
                  }}
                >
                  <IntlMessages id="user.login-title" />
                </Button>
              </Link>
            ) : (
              <Dropdown className="dropdown-menu-right mr-2">
                <Dropdown.Toggle className="p-0" as={CustomDropdownToggle} id='topnavDropdown'>
                  <span color='black' className="name text-dark mr-2">{currentUser?.name}</span>
                  <span className="position-relative d-inline-block">
                    <img
                      className='m-0'
                      alt={intl.formatMessage({ id: 'general.profile' })}
                      src={currentUser?.avatarUrl ? currentUser.avatarUrl : '/assets/img/profiles/l-1.jpg'}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = '/assets/img/profiles/l-1.jpg';
                      }}
                    />
                    {hasPlayingQuests &&
                      <span
                        className="badge badge-pill badge-play position-absolute d-flex align-items-center justify-content-center"
                        style={{ top: '-0.6rem', right: '-0.45rem', width: '1.15rem', height: '1.15rem', padding: 0 }}
                        aria-label={intl.formatMessage({ id: 'route.playing' })}
                      >
                        <PlayCircleOutlineIcon size={14} color="white" />
                      </span>
                    }
                  </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="mt-1" right>
                  {hasPlayingQuests &&
                    <Dropdown.Item onClick={() => handlePlaying()}>
                      <PlayCircleOutlineIcon
                        size={18}
                        className="mr-2 accent-color"
                        aria-label={intl.formatMessage({ id: 'route.playing' })}
                      />
                      <IntlMessages id="route.playing" />
                    </Dropdown.Item>
                  }
                  <Dropdown.Item onClick={() => handleProfile()}>
                    <AccountOutlineIcon size={18} className="mr-2" />
                    <IntlMessages id="menu.profile" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleSettings()}>
                    <SettingsIcon size={18} className="mr-2" />
                    <IntlMessages id="menu.settings" />
                  </Dropdown.Item>
                  {(currentUser?.isAdmin || currentUser?.isTranslator) && (
                    <Dropdown.Item onClick={() => handleTranslations()}>
                      <TranslateIcon size={18} className="mr-2" />
                      <IntlMessages id="translations.page.title" />
                    </Dropdown.Item>
                  )}
                  <Dropdown.Item onClick={() => handleAbout()}>
                    <InformationOutlineIcon size={18} className="mr-2" />
                    <IntlMessages id="nav.about" />
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => handleContact()}>
                    <EmailOutlineIcon size={18} className="mr-2" />
                    <IntlMessages id="nav.contact" />
                  </Dropdown.Item>
                  <Dropdown.Item divider />
                  <Dropdown.Item onClick={handleLogout}>
                    <LogoutVariantIcon size={18} className="mr-2" />
                    <IntlMessages id="menu.sign-out" />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </div>
      </nav>
      {currentUser && (!currentUser.isVerified || showVerifyBanner) &&
        <div className={`d-flex top-sticky-notification ${(verificationEmailResent || verificationEmailResentError) && 'disabled'}`} >
          {
            !verificationEmailResent && !verificationEmailResentError
              ?
              <div className='my-auto mx-auto' onClick={() => {
                resendVerificationEmail({
                  variables: {
                    emailId: currentUser.userEmails.nodes[0].id,
                  }
                }).then(() => {
                  setVerificationEmailResent(true);
		}).catch((error) => {
                  console.error('Failed to resend verification email: ', error);
                  setVerificationEmailResentError(true);
                })

              }}>
                <IntlMessages id='user.verify-e-mail-reminder' />
              </div>
              : verificationEmailResentError
                ?
                // error messages
                <div className='my-auto mx-auto'>
                  <IntlMessages id='general.loading-error' />
                </div>
                :
                // success message
                <div className='my-auto mx-auto'>
                  <IntlMessages id='user.verify-e-mail-resent' values={{ userEmail: currentUser.userEmails.nodes[0].email }} />
                </div>
          }

        </div>
      }
      <InfoModal
        description={ChangeLanguageModalContent()}
        showInfoModal={showInfoModal}
        setShowInfoModal={setShowInfoModal}
        handleConfirm={handleConfirm}
      />
      <Modal
        show={showPlayingQuestsModal}
        onHide={() => setShowPlayingQuestsModal(false)}
        className="rounded-modal playing-quests-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <PlayCircleOutlineIcon size={24} className="mr-2 accent-color" />
            <span><IntlMessages id="route.playing.modal-title" /></span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-3"><IntlMessages id="route.playing.modal-description" /></p>
          <div className="d-flex flex-column">
            {playingQuests.map((quest) => (
              <Link
                key={`${quest.mode}-${quest.id}`}
                to={quest.url}
                className="d-flex justify-content-between align-items-center py-2 border-bottom text-decoration-none"
                onClick={() => setShowPlayingQuestsModal(false)}
              >
                <span className="d-flex align-items-baseline flex-grow-1" style={{ minWidth: 0 }}>
                  <span className="text-truncate">{quest.name}</span>
                  {quest.lastActiveAt &&
                    <span className="text-muted small ml-2 text-nowrap">
                      <FormattedTime value={quest.lastActiveAt} month="short" day="2-digit" hour="2-digit" minute="2-digit" />
                    </span>
                  }
                </span>
                <span className="badge badge-outline-orange playing-quest-mode-badge ml-3">
                  <IntlMessages id={quest.mode === 'multiplayer' ? 'route.quest.multiplayer' : 'route.quest.single-player'} />
                </span>
              </Link>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

TopNav.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.any,
  }),
};

export default TopNav;
